// Path: /home/ladmin/ESTATA/apps/0001-estata.com/frontend/src/components/ui/LanguageSwitcher.tsx
// Version: v1.5.1
// Description: Desktop - sticky rechter Rand ohne Fuge (fixed position)
import { Check, ChevronDown, Globe } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLanguage } from '../../lang/index.lang.tsx';

// ==========================================
// ZENTRALE KONSTANTEN
// ==========================================
const HEADER_HEIGHT = 0; // px - MUSS mit HeaderDesktop übereinstimmen!
const ICON_SIZE = 20; // px (w-6 h-6)
const ICON_SIZE_SMALL = 20; // px (w-4 h-4)
const ICON_SIZE_LARGE = 20; // px (w-8 h-8)
const CHEVRON_SIZE = 20; // px (w-5 h-5)
const FONT_SIZE_NORMAL = 15; // px
const FONT_SIZE_SMALL = 12; // px
const FONT_SIZE_TINY = 13; // px
const DROPDOWN_WIDTH = 275; // px (w-80 = 320px)
const COLOR_ACCENT = '#37acdc'; // ESTATA Blau
const COLOR_BORDER = '#374151'; // gray-700
const COLOR_BG_HOVER = '#111827'; // gray-900

// ==========================================
// INTERFACES
// ==========================================
interface LanguageSwitcherProps {
  variant?: 'desktop' | 'mobile';
  onLanguageChange?: (languageCode: string) => void;
  className?: string;
}

// ==========================================
// STYLE HELPERS
// ==========================================
const getIconStyle = (size: number): React.CSSProperties => ({
  width: `${size}px`,
  height: `${size}px`
});

// ==========================================
// COMPONENT
// ==========================================
const LanguageSwitcher: React.FC<LanguageSwitcherProps> = ({ 
  variant = 'desktop', 
  onLanguageChange, 
  className = '' 
}) => {
  const { currentLanguage, changeLanguage, languages, t } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const handleLanguageSelect = useCallback((languageCode: string) => {
    changeLanguage(languageCode);
    setIsOpen(false);
    if (onLanguageChange) {
      onLanguageChange(languageCode);
    }
  }, [changeLanguage, onLanguageChange]);

  const toggleDropdown = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  // Position dropdown - sticky am rechten Viewport-Rand ohne Fuge
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const menuRight = buttonRect.left + DROPDOWN_WIDTH;
      
      if (menuRight > viewportWidth) {
        // Menü würde über rechten Rand hinausragen -> fixed am rechten Rand
        setMenuStyle({
          position: 'fixed',
          top: `${buttonRect.bottom}px`,
          right: 0,
          left: 'auto',
          width: `${DROPDOWN_WIDTH}px`
        });
      } else {
        // Normal links ausgerichtet (absolute)
        setMenuStyle({
          position: 'absolute',
          top: '100%',
          left: 0,
          right: 'auto',
          width: `${DROPDOWN_WIDTH}px`
        });
      }
    }
  }, [isOpen]);

  // Update position on resize
  useEffect(() => {
    const handleResize = () => {
      if (isOpen && buttonRef.current) {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const menuRight = buttonRect.left + DROPDOWN_WIDTH;
        
        if (menuRight > viewportWidth) {
          setMenuStyle({
            position: 'fixed',
            top: `${buttonRect.bottom}px`,
            right: 0,
            left: 'auto',
            width: `${DROPDOWN_WIDTH}px`
          });
        } else {
          setMenuStyle({
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 'auto',
            width: `${DROPDOWN_WIDTH}px`
          });
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(target) &&
        buttonRef.current &&
        !buttonRef.current.contains(target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isOpen]);

  // Close dropdown on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  // Close dropdown when scrolling (Header einfährt)
  useEffect(() => {
    const handleScroll = () => {
      if (isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      window.addEventListener('scroll', handleScroll, { passive: true });
    }

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isOpen]);

  const currentLang = languages[currentLanguage];  
  const allLanguages = Object.entries(languages);

  // Mobile Variant
  if (variant === 'mobile') {
    return (
      <div className={`relative ${className}`}>
        <button
          ref={buttonRef}
          onClick={toggleDropdown}
          className="flex items-center justify-between w-full text-white hover:text-[#37acdc] transition-colors"
          style={{
            paddingLeft: '24px',
            paddingRight: '24px',
            paddingTop: '16px',
            paddingBottom: '16px',
            borderTop: `1px solid ${COLOR_BORDER}`
          }}
          aria-expanded={isOpen}
          aria-haspopup="true"
          aria-label={`${t('language.selectLanguage')} - ${t('language.currentLanguage')}: ${currentLang.name}`}
        >
          <div className="flex items-center">
            <div className="relative inline-block mr-3">
              <Globe style={getIconStyle(ICON_SIZE)} aria-hidden="true" />
            </div>
            <span style={{ fontSize: `${FONT_SIZE_NORMAL}px` }}>{currentLang?.name || 'Global'}</span>
          </div>
          <ChevronDown 
            style={getIconStyle(CHEVRON_SIZE)}
            className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          />
        </button>

        {/* Mobile Dropdown */}
        {isOpen && (
          <>
            <div 
              className="fixed inset-0 z-40 bg-transparent"
              onClick={() => setIsOpen(false)}
              aria-hidden="true"
            />
            
            <div
              ref={dropdownRef}
              className="absolute bottom-full left-0 right-0 bg-black shadow-lg z-50 overflow-hidden"
              style={{ 
                borderTop: `1px solid ${COLOR_BORDER}`,
              }}
              role="menu"
            >
              {/* Current Language Header */}
              <div 
                className="bg-gray-900"
                style={{
                  paddingLeft: '24px',
                  paddingRight: '24px',
                  paddingTop: '16px',
                  paddingBottom: '16px',
                  borderBottom: `1px solid ${COLOR_BORDER}`
                }}
              >
                <div className="flex items-center">
                  <div className="relative inline-block mr-3">
                    <Globe style={{ ...getIconStyle(ICON_SIZE), color: COLOR_ACCENT }} aria-hidden="true" />
                  </div>
                  <div className="flex-1">
                    <div 
                      className="font-medium"
                      style={{ fontSize: `${FONT_SIZE_NORMAL}px`, color: COLOR_ACCENT }}
                    >
                      {currentLang.name}
                    </div>
                    <div 
                      className="text-gray-400"
                      style={{ fontSize: `${FONT_SIZE_SMALL}px` }}
                    >
                      {t('language.currentLanguage')}
                    </div>
                  </div>
                  <Check style={{ ...getIconStyle(CHEVRON_SIZE), color: COLOR_ACCENT }} />
                </div>
              </div>

              {/* Other Languages */}
              <div style={{ maxHeight: '192px', overflowY: 'auto' }}>
                {allLanguages
                  .filter(([key]) => key !== currentLanguage)
                  .map(([key, lang]) => (
                    <button
                      key={key}
                      onClick={() => handleLanguageSelect(key)}
                      className="flex items-center w-full text-left transition-colors text-white hover:text-[#37acdc] hover:bg-gray-900 group"
                      style={{
                        paddingLeft: '24px',
                        paddingRight: '24px',
                        paddingTop: '16px',
                        paddingBottom: '16px',
                        borderBottom: `1px solid ${COLOR_BORDER}`
                      }}
                      role="menuitem"
                    >
                      <div className="relative inline-block mr-1">
                        <Globe style={getIconStyle(ICON_SIZE)} aria-hidden="true" />
                      </div>
                      <div className="flex-1">
                        <div 
                          className="font-medium"
                          style={{ fontSize: `${FONT_SIZE_NORMAL}px` }}
                        >
                          {lang.name}
                        </div>
                        <div 
                          className="opacity-75"
                          style={{ fontSize: `${FONT_SIZE_SMALL}px` }}
                        >
                          {lang.name_en}
                        </div>
                      </div>
                    </button>
                  ))}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // Desktop Variant
  return (
    <div className={`relative h-full flex items-center ${className}`}>
      <button
        ref={buttonRef}
        onClick={toggleDropdown}
        className="flex items-center space-x-2 text-gray-300 hover:text-white duration-200 font-normal h-full"
        style={{ paddingLeft: '10px', paddingRight: '10px' }}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={`${t('language.selectLanguage')} - ${t('language.currentLanguage')}: ${currentLang.name}`}
      >
        <div className="relative inline-block">
          <Globe style={getIconStyle(ICON_SIZE)} aria-hidden="true" />
        </div>
        <span style={{ fontSize: `${FONT_SIZE_NORMAL}px` }} className="font-medium">
          {currentLang?.name}
        </span>
        <ChevronDown 
          style={getIconStyle(ICON_SIZE_SMALL)}
          className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>

      {/* Desktop Dropdown */}
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
            style={{ background: 'transparent' }}
            aria-hidden="true"
          />
          
          <div
            ref={dropdownRef}
            className="bg-black z-50"
            style={menuStyle}
            role="menu"
          >
            {/* Current Language Section */}
            <div 
              className="flex items-center w-full py-3 text-white font-normal"
              style={{ paddingLeft: '10px', paddingRight: '10px' }}
            >
              <div className="relative inline-block" style={{ marginRight: '8px' }}>
                <Globe style={{ ...getIconStyle(ICON_SIZE), color: COLOR_ACCENT }} />
              </div>
              <div className="flex-1">
                <div 
                  className="font-semibold"
                  style={{ fontSize: `${FONT_SIZE_NORMAL}px`, color: COLOR_ACCENT }}
                >
                  {currentLang.name}
                </div>
                <div 
                  className="text-gray-400"
                  style={{ fontSize: `${FONT_SIZE_SMALL}px` }}
                >
                  {t('language.currentLanguage')}
                </div>
              </div>
              <Check style={{ ...getIconStyle(ICON_SIZE), color: COLOR_ACCENT }} />
            </div>

            {/* Available Languages Section */}
            <div>
              <div 
                className="flex items-center w-full py-2 text-gray-500 uppercase"
                style={{ 
                  fontSize: `${FONT_SIZE_TINY}px`,
                  paddingLeft: '10px',
                  paddingRight: '10px'
                }}
              >
                <span>{t('language.selectLanguage')}</span>
              </div>
              
              <div style={{ maxHeight: 'calc(100vh - 150px)', overflowY: 'auto' }}>
                {allLanguages
                  .filter(([key]) => key !== currentLanguage)
                  .map(([key, lang]) => (
                    <button
                      key={key}
                      onClick={() => handleLanguageSelect(key)}
                      className="flex items-center w-full py-3 font-medium text-white transition-all duration-200 hover:text-[#37acdc] text-left bg-black"
                      style={{ 
                        fontSize: `${FONT_SIZE_NORMAL}px`,
                        paddingLeft: '10px',
                        paddingRight: '10px'
                      }}
                    >
                      <div className="relative inline-block" style={{ marginRight: '8px' }}>
                        <Globe style={getIconStyle(ICON_SIZE)} />
                      </div>
                      <div className="flex-1">
                        <div 
                          className="font-normal"
                          style={{ fontSize: `${FONT_SIZE_NORMAL}px` }}
                        >
                          {lang.name}
                        </div>
                        <div 
                          className="opacity-50"
                          style={{ fontSize: `${FONT_SIZE_SMALL}px` }}
                        >
                          {lang.name_en}
                        </div>
                      </div>
                    </button>
                  ))}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default LanguageSwitcher;
